import { useRenderProps } from "@/components/vendor/useRenderProps.ts";
import { mcn, mergeProps, type BaseProps } from "@/utils/baseProps.ts";
import { filterDOMProps, useViewportSize } from "@react-aria/utils";
import { useOverlayTriggerState, type OverlayTriggerState } from "@react-stately/overlays";
import { forwardRef, useContext, useRef, type ReactNode, type RefObject } from "react";
import { Overlay, useIsSSR, useModalOverlay, useObjectRef, type OverlayProps } from "react-aria";
import {
    ModalContext,
    OverlayTriggerStateContext,
    Provider,
    useContextProps,
    type ModalOverlayProps,
} from "react-aria-components";

//
// NB: This file re-implements `ModalOverlay` from `react-aria` to add support for disabling focus management.
// This is needed for eg. Razorpay, which opens its own dialog which breaks if focus is trapped (due to not allowing to focus inputs inside that dialog).
//
// Source: https://github.com/adobe/react-spectrum/blob/react-aria%403.37.0/packages/react-aria-components/src/Modal.tsx
//

export interface XModalOverlayProps extends ModalOverlayProps {
    disableFocusManagement?: OverlayProps["disableFocusManagement"];
}

export const XModalOverlay = forwardRef<HTMLDivElement, ModalOverlayProps>((props, ref) => {
    [props, ref] = useContextProps(props, ref, ModalContext);
    let contextState = useContext(OverlayTriggerStateContext);
    let localState = useOverlayTriggerState(props);
    let state = props.isOpen != null || props.defaultOpen != null || !contextState ? localState : contextState;

    let objectRef = useObjectRef(ref);
    let modalRef = useRef<HTMLDivElement>(null);

    let isExiting = props.isExiting || false;
    let isSSR = useIsSSR();

    if ((!state.isOpen && !isExiting) || isSSR) {
        return null;
    }

    return <XModalOverlayInner {...props} state={state} overlayRef={objectRef} modalRef={modalRef} />;
});

interface ModalOverlayInnerProps extends XModalOverlayProps {
    overlayRef: RefObject<HTMLDivElement | null>;
    modalRef: RefObject<HTMLDivElement | null>;
    state: OverlayTriggerState;
}

function XModalOverlayInner({ disableFocusManagement, ...props }: ModalOverlayInnerProps) {
    let modalRef = props.modalRef;
    let { state } = props;
    let { modalProps, underlayProps } = useModalOverlay(props, state, modalRef);

    let renderProps = useRenderProps({
        ...props,
        defaultClassName: "react-aria-ModalOverlay",
        values: {
            isEntering: false,
            isExiting: false,
            state,
        },
    });

    let viewport = useViewportSize();
    let style = {
        ...renderProps.style,
        "--visual-viewport-height": viewport.height + "px",
    };

    return (
        <Overlay isExiting={props.isExiting} disableFocusManagement={disableFocusManagement}>
            <div
                {...mergeProps(filterDOMProps(props as any) as any, underlayProps)}
                {...renderProps}
                style={style}
                ref={props.overlayRef}
            >
                <div {...modalProps}>
                    <Provider values={[[OverlayTriggerStateContext, state]]}>{renderProps.children}</Provider>
                </div>
            </div>
        </Overlay>
    );
}

export function XModalContent({ children, ...props }: { children: ReactNode } & BaseProps) {
    return <div {...mcn("react-aria-Modal", props)}>{children}</div>;
}
